import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosCatalogos, version, type Categoria } from './datos';

// «Configuraciones - Catálogos (Nueva categoría)» y «(Editar categoría)» del
// Figma: la misma ventana con un solo campo. Editar pide la versión del listado
// (#77): si otra persona la renombró mientras tanto, choca en vez de pisarla.

export type EdicionCategoria = { tipo: 'nueva' } | { tipo: 'editar'; categoria: Categoria } | null;

interface Props {
  edicion: EdicionCategoria;
  onCerrar: () => void;
}

export function VentanaCategoria({ edicion, onCerrar }: Props) {
  // La clave reinicia el formulario al abrir otra categoría, o al volver a abrir.
  const clave = edicion === null ? 'cerrada' : edicion.tipo === 'nueva' ? 'nueva' : String(edicion.categoria.id);
  return <Contenido key={clave} edicion={edicion} onCerrar={onCerrar} />;
}

function Contenido({ edicion, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosCatalogos();
  const categoria = edicion?.tipo === 'editar' ? edicion.categoria : null;

  const form = useFormulario<{ name: string }>({
    inicial: { name: categoria?.name ?? '' },
    guardar: (v) =>
      categoria
        ? cambios.renombrarCategoria(String(categoria.id), version(categoria.version), v.name)
        : cambios.crearCategoria(v.name),
    camposPorCodigo: { 'catalog_category.nombre_duplicado': 'name' },
  });

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: categoria
        ? t('admin.catalogos.categorias.aviso.guardada', { nombre })
        : t('admin.catalogos.categorias.aviso.creada', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={categoria ? t('admin.catalogos.categorias.editar') : t('admin.catalogos.categorias.nueva')}
      info={t('admin.catalogos.categorias.ventanaInfo')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {categoria ? t('admin.catalogos.guardarCambios') : t('app.guardar')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.catalogos.nombre')}
        obligatorio
        maximo={250}
        placeholder={t('admin.catalogos.categorias.ejemplo')}
        autoComplete="off"
        ancho="completo"
      />
    </Ventana>
  );
}
